import { useInstagramFeed, type InstagramPost } from "../api/hooks";
import type { Gym } from "../api/hooks";
import { isLocal } from "../env";

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
    </svg>
  );
}

function PostCard({ post, className = "" }: { post: InstagramPost; className?: string }) {
  return (
    <a
      href={post.permalink}
      target="_blank"
      rel="noopener noreferrer"
      className={`group relative aspect-square block overflow-hidden rounded-sm ${className}`}
      aria-label={post.caption}
    >
      <img
        src={post.imageUrl}
        alt=""
        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
        loading="lazy"
      />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/55 p-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
        <InstagramIcon className="h-5 w-5 text-white" />
        <p className="line-clamp-3 text-center text-[11px] leading-snug text-white/90">
          {post.caption}
        </p>
      </div>
    </a>
  );
}

function FeedSkeleton() {
  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-4">
        <div className="space-y-1.5">
          <div className="h-5 w-36 animate-pulse rounded bg-black/5" />
          <div className="h-3.5 w-28 animate-pulse rounded bg-black/5" />
        </div>
        <div className="h-9 w-28 animate-pulse rounded-md bg-black/5" />
      </div>
      <div className="grid grid-cols-2 gap-1 md:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className={`aspect-square animate-pulse rounded-sm bg-black/5 ${i >= 4 ? "hidden md:block" : ""}`}
          />
        ))}
      </div>
    </div>
  );
}

function GymFeed({ gym }: { gym: Gym }) {
  const { data, isLoading } = useInstagramFeed(gym.slug);

  if (isLoading) return <FeedSkeleton />;
  if (!data?.posts.length) return null;

  const profileUrl = data.handle
    ? `https://www.instagram.com/${data.handle.replace("@", "")}/`
    : "https://www.instagram.com/";

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-4">
        <div>
          <p className="font-display text-xl font-bold uppercase">{gym.name}</p>
          <div className="flex items-center gap-2">
            {data.handle && <p className="text-sm text-ink-400">{data.handle}</p>}
            {isLocal && data.isMock && (
              <span className="rounded bg-yellow-500/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-yellow-300">
                Mock
              </span>
            )}
          </div>
        </div>
        <a
          href={profileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-ink-300 px-3 py-2 text-xs font-semibold text-ink-900 transition hover:border-brand-500 hover:text-brand-500"
        >
          <InstagramIcon className="h-4 w-4" />
          Voir le profil
        </a>
      </div>

      <div className="grid grid-cols-2 gap-1 md:grid-cols-3">
        {data.posts.map((post, i) => (
          <PostCard key={post.id} post={post} className={i >= 4 ? "hidden md:block" : ""} />
        ))}
      </div>
    </div>
  );
}

export function InstagramSection({ gyms }: { gyms?: Gym[] }) {
  if (!gyms?.length) return null;

  return (
    <section className="section bg-paper text-ink-900">
      <div className="container">
        <div className="max-w-2xl">
          <span className="eyebrow flex items-center gap-2">
            <InstagramIcon className="h-4 w-4" />
            Instagram
          </span>
          <h2 className="h-section mt-2">Suivez notre quotidien</h2>
          <p className="mt-3 text-ink-500">
            Les WODs, les PRs, les nouveautés — tout ce qui se passe dans nos boxes, en direct.
          </p>
        </div>

        <div className="mt-10 grid gap-12 md:grid-cols-2">
          {gyms.map((gym) => (
            <GymFeed key={gym.id} gym={gym} />
          ))}
        </div>
      </div>
    </section>
  );
}
